import { useCentralReady, useCentralRecovery } from '../../context/CentralReadyContext';
import { SuiteProductAccess } from '../../components/suite/SuiteProductAccess';
import { redirectToTrialBilling } from '../../helpers/trialPayment';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { EventBus } from '../../helpers/EventBus';
import { appEnv } from '../../config/env';
import { centralEnabled, cmsSession, refreshCmsSession, accountsPortal, type Workspace } from '../../services/centralAuth';
import styles from './centralAccounts.module.scss';

export function CentralSiteAccess({ children }: { children: ReactNode }) {
  const location = useLocation();
  const sessionReady = useCentralReady();
  const recovering = useCentralRecovery();
  const selected = () => cmsSession()?.context.workspaces.find(x => x.businessId === localStorage.getItem(appEnv.storageCurrentSiteKey));
  const [site, setSite] = useState<Workspace | undefined>(selected);
  const [ready, setReady] = useState(!centralEnabled);
  const [error, setError] = useState(false);
  const [accessLost, setAccessLost] = useState(false);
  const verifiedSite = useRef<string>();
  const verified = useRef(false);
  const opened = useRef(false);
  const retry = useRef<() => void>(() => {});
  useEffect(() => {
    if (!centralEnabled || !sessionReady || recovering) return;
    let active = true;
    let sequence = 0;
    const load = async () => {
      const request = ++sequence;
      try {
        await refreshCmsSession();
        if (active && request === sequence) {
          const current = verifiedSite.current
            ? cmsSession()?.context.workspaces.find(x => x.businessId === verifiedSite.current)
            : selected();
          const path = location.pathname.toLowerCase();
          if (!verified.current && !path.startsWith('/systemadmin/') && path !== '/users/registernewsite' && redirectToTrialBilling(current, accountsPortal)) return;
          // Preserve the mounted workspace after its first successful verification.
          if (current || !verified.current) setSite(current);
          setError(false);
          setAccessLost(!!verifiedSite.current && !current);
          if (current) verifiedSite.current = current.businessId;
          if (current?.entitlement.hasAccess) opened.current = true;
          verified.current = true; setReady(true);
        }
      } catch {
        if (active && request === sequence) { setError(true); setReady(true); }
      }
    };
    retry.current = () => { void load(); };
    const switched = () => { verified.current = false; opened.current = false; verifiedSite.current = undefined; setAccessLost(false); setError(false); setReady(false); setSite(selected()); void load(); };
    const focused = () => { void load(); };
    void load();
    const timer = window.setInterval(() => void load(), 60000);
    const bus = EventBus.getInstance();
    bus.subscribe(EventBus.Event.OnSiteSelected, switched);
    window.addEventListener('focus', focused);
    window.addEventListener('cms-access-changed', focused);
    return () => { active = false; ++sequence; window.clearInterval(timer); bus.unsubscribe(EventBus.Event.OnSiteSelected, switched); window.removeEventListener('focus', focused); window.removeEventListener('cms-access-changed', focused); };
  }, [location.pathname, sessionReady, recovering]);
  if (!centralEnabled) return <>{children}</>;
  if (recovering && !opened.current) return null;
  if ((!sessionReady || !ready) && !opened.current) return null;
  if (error && !verified.current) return <section className={styles.accessNotice} role="alert"><h2>Unable to check site access</h2><p>Please retry before continuing.</p><button className="btn btn-primary" onClick={() => retry.current()}>Retry</button></section>;
  if (!site && cmsSession()?.context.workspaces.length === 0 && !location.pathname.toLowerCase().startsWith('/systemadmin/') && location.pathname.toLowerCase() !== '/users/registernewsite') return <SuiteProductAccess product="cms" selectionHref="/select-site" />;
  // Account-wide administration and site selection do not belong to a selected site's subscription.
  if (!site || location.pathname.toLowerCase().startsWith('/systemadmin/') || location.pathname.toLowerCase() === '/users/registernewsite') return <>{children}</>;
  const access = site.entitlement;
  const expired = access.reason === 'Trial' && !!access.trialEndsAt && Date.parse(access.trialEndsAt) <= Date.now();
  const locked = accessLost || !access.hasAccess || expired;
  const trialWarning = access.showTrialWarning || (access.reason === 'Trial' && !!access.trialEndsAt && Date.parse(access.trialEndsAt) <= Date.now() + 7 * 86400000);
  const billing = `${accountsPortal}/subscriptions/${site.resourceId}?accountId=${encodeURIComponent(site.accountId)}`;
  return <>
    {error && !recovering && <section className={styles.accessNotice} role="status"><p>Unable to verify site access. Your open work has been kept. Saving may be unavailable until access can be verified.</p><button className="btn btn-outline-secondary" onClick={() => retry.current()}>Retry access check</button></section>}
    {!recovering && (locked || trialWarning) && <section className={styles.accessNotice} role={locked ? 'alert' : 'status'}>
      <h2>{locked ? 'Site access is locked' : 'Your site trial is ending soon'}</h2>
      <p>{locked ? 'Choose and pay for a subscription in Accounts to continue, or ask your billing account owner to review any account restrictions. Your open work has been kept on this screen. Editing is paused until access is restored.' : `Your trial ends on ${new Date(access.trialEndsAt!).toLocaleDateString()}. Choose and pay for a subscription to keep using CMS. You will not be charged automatically.`}</p>
      <div className={styles.accountActions}>{site.canManageBilling ? <a className="btn btn-primary" href={billing} target="_blank" rel="noopener noreferrer">Choose subscription in Accounts</a> : <span>Contact your billing account owner to arrange payment.</span>}{locked && <button className="btn btn-outline-secondary" onClick={() => retry.current()}>Check access again</button>}{locked && <Link className="btn btn-outline-secondary" to="/select-site">Select another site</Link>}</div>
    </section>}
    <div className={styles.accessBoundary} {...(locked ? { inert: '' } : {})} aria-disabled={locked || undefined}>
      {(!locked || opened.current) && children}
    </div>
  </>;
}
